import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/auth';
import { useRoles } from '../../hooks/useRoles';
import { 
  getAllUsers, 
  updateUserRole, 
  deactivateUser, 
  deleteUser,
  updateUserProfile
} from '../../services/userService';
import { ROLES, ROLE_LABELS } from '../../constants/roles';
import './AdminDashboard.css';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const { userRole, roleLabel, canManageUsers, canManageRoles, isSuperAdmin } = useRoles();
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [profileForm, setProfileForm] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    address: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = users.filter(user => 
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredUsers(filtered);
    } else {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await getAllUsers();
      setUsers(allUsers);
      setFilteredUsers(allUsers);
    } catch (error) {
      setError('Failed to load users: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      setActionLoading(true);
      await updateUserRole(userId, newRole, currentUser.uid);
      await loadUsers(); // Reload users
      setShowRoleModal(false);
      setSelectedUser(null);
    } catch (error) {
      setError('Failed to update user role: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      setActionLoading(true);
      await deactivateUser(userId, currentUser.uid);
      await loadUsers(); // Reload users
    } catch (error) {
      setError('Failed to deactivate user: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      setActionLoading(true);
      await deleteUser(userId);
      await loadUsers(); // Reload users
      setShowDeleteModal(false);
      setSelectedUser(null);
    } catch (error) {
      setError('Failed to delete user: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const openRoleModal = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openProfileModal = (user) => {
    setSelectedUser(user);
    setProfileForm({
      displayName: user.displayName || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      address: user.address || ''
    });
    setShowProfileModal(true);
  };

  const handleProfileUpdate = async () => {
    try {
      setActionLoading(true);
      await updateUserProfile(selectedUser.uid, {
        displayName: profileForm.displayName,
        email: profileForm.email,
        phoneNumber: profileForm.phoneNumber,
        address: profileForm.address
      });
      await loadUsers(); // Reload users
      setShowProfileModal(false);
      setSelectedUser(null);
      setProfileForm({ displayName: '', email: '', phoneNumber: '', address: '' });
    } catch (error) {
      setError('Failed to update user profile: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp.seconds * 1000).toLocaleDateString();
  };

  if (!canManageUsers) {
    return (
      <div className="admin-dashboard">
        <div className="admin-content">
          <h1>Access Denied</h1>
          <p>You don't have permission to access the admin dashboard.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-content">
        <div className="admin-header">
          <h1>Admin Dashboard</h1>
          <p>Welcome, {currentUser?.email} ({roleLabel})</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
            <button onClick={() => setError('')} className="error-close">×</button>
          </div>
        )}

        <div className="admin-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search users by email or name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <button 
            onClick={loadUsers} 
            disabled={loading}
            className="refresh-button"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        <div className="users-table-container">
          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.uid}>
                    <td>
                      <div className="user-info">
                        <div className="user-avatar">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.displayName} />
                          ) : (
                            <div className="avatar-placeholder">
                              {user.displayName?.charAt(0) || user.email?.charAt(0)}
                            </div>
                          )}
                        </div>
                        <div className="user-details">
                          <div className="user-name">{user.displayName || 'No name'}</div>
                          <div className="user-email">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`role-badge role-${user.role}`}>
                        {ROLE_LABELS[user.role]}
                      </span>
                    </td>
                    <td>
                      <span className={`status-badge ${user.isActive ? 'active' : 'inactive'}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(user.createdAt)}</td>
                    <td>{formatDate(user.lastLoginAt)}</td>
                    <td>
                      <div className="action-buttons">
                        {isSuperAdmin && (
                          <button
                            onClick={() => openProfileModal(user)}
                            className="action-button edit"
                            disabled={actionLoading}
                          >
                            Edit Profile
                          </button>
                        )}
                        {canManageRoles && user.uid !== currentUser.uid && (
                          <button
                            onClick={() => openRoleModal(user)}
                            className="action-button edit"
                            disabled={actionLoading}
                          >
                            Change Role
                          </button>
                        )}
                        {user.isActive && user.uid !== currentUser.uid && (
                          <button
                            onClick={() => handleDeactivateUser(user.uid)}
                            className="action-button deactivate"
                            disabled={actionLoading}
                          >
                            Deactivate
                          </button>
                        )}
                        {isSuperAdmin && user.uid !== currentUser.uid && (
                          <button
                            onClick={() => openDeleteModal(user)}
                            className="action-button delete"
                            disabled={actionLoading}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Role Change Modal */}
        {showRoleModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>Change User Role</h2>
              <p>Change role for: {selectedUser.email}</p>
              <div className="role-options">
                {Object.entries(ROLES).map(([key, value]) => (
                  <button
                    key={value}
                    onClick={() => handleRoleChange(selectedUser.uid, value)}
                    className={`role-option ${selectedUser.role === value ? 'selected' : ''}`}
                    disabled={actionLoading}
                  >
                    {ROLE_LABELS[value]}
                  </button>
                ))}
              </div>
              <div className="modal-actions">
                <button
                  onClick={() => setShowRoleModal(false)}
                  className="cancel-button"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal">
              <h2>Delete User</h2>
              <p>Are you sure you want to delete {selectedUser.email}?</p>
              <p className="warning">This action cannot be undone.</p>
              <div className="modal-actions">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="cancel-button"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteUser(selectedUser.uid)}
                  className="delete-button"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile Edit Modal */}
        {showProfileModal && selectedUser && (
          <div className="modal-overlay">
            <div className="modal profile-modal">
              <h2>Edit User Profile</h2>
              <p>Editing profile for: {selectedUser.email}</p>
              
              <div className="profile-form">
                <div className="form-group">
                  <label htmlFor="displayName">Full Name</label>
                  <input
                    type="text"
                    id="displayName"
                    value={profileForm.displayName}
                    onChange={(e) => setProfileForm({...profileForm, displayName: e.target.value})}
                    placeholder="Enter full name"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={profileForm.email}
                    onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                    placeholder="Enter email address"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phoneNumber">Phone Number</label>
                  <input
                    type="tel"
                    id="phoneNumber"
                    value={profileForm.phoneNumber}
                    onChange={(e) => setProfileForm({...profileForm, phoneNumber: e.target.value})}
                    placeholder="Enter phone number"
                    className="form-input"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">Address</label>
                  <textarea
                    id="address"
                    value={profileForm.address}
                    onChange={(e) => setProfileForm({...profileForm, address: e.target.value})}
                    placeholder="Enter address"
                    className="form-input"
                    rows="3"
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowProfileModal(false);
                    setSelectedUser(null);
                    setProfileForm({ displayName: '', email: '', phoneNumber: '', address: '' });
                  }}
                  className="cancel-button"
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleProfileUpdate}
                  className="save-button"
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard; 